import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, LogOut, ChevronDown } from 'lucide-react';
import { supabase } from '../services/api';

function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => { loadUser(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownOpen(false);
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const handleLogout = async () => { await supabase.auth.signOut(); navigate('/login'); };
  const isActivePath = (path) => location.pathname === path;

  const getUserDisplayName = () => {
    if (user?.user_metadata?.first_name && user?.user_metadata?.last_name)
      return `${user.user_metadata.first_name} ${user.user_metadata.last_name}`;
    if (user?.user_metadata?.full_name) return user.user_metadata.full_name;
    return 'User';
  };

  const getUserInitials = () => {
    const f = user?.user_metadata?.first_name || '';
    const l = user?.user_metadata?.last_name || '';
    if (f && l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
    if (user?.user_metadata?.full_name) {
      const n = user.user_metadata.full_name.split(' ');
      return n.length >= 2 ? `${n[0].charAt(0)}${n[1].charAt(0)}`.toUpperCase() : n[0].charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const navLinks = [
    { path: '/dashboard', label: t('nav.dashboard') },
    { path: '/settings', label: t('nav.settings') },
  ];

  return (
    <header className="sticky top-0 z-50" style={{ background: '#0a1628', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">

          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="TAHLEEL.ai" className="w-8 h-8" />
            <span className="text-base font-bold text-white tracking-wide">TAHLEEL<span style={{ color: '#f59e0b' }}>.ai</span></span>
            <span className="hidden sm:inline text-xs font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>&#1578;&#1581;&#1604;&#1610;&#1604;</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}
                className="px-3.5 py-1.5 rounded-md text-sm font-medium transition-all"
                style={{
                  color: isActivePath(link.path) ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                  background: isActivePath(link.path) ? 'rgba(245,158,11,0.08)' : 'transparent',
                }}>
                {link.label}
              </Link>
            ))}
            <button onClick={handleLogout}
              className="px-3.5 py-1.5 rounded-md text-sm font-medium transition-all"
              style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t('auth.logout')}
            </button>
          </nav>

          {/* Right — user initials */}
          <div className="hidden md:flex items-center">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#f59e0b' }}>
              <span className="font-bold text-xs" style={{ color: '#0a1628' }}>{getUserInitials()}</span>
            </div>
          </div>

          {/* Mobile */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-md" style={{ color: '#fff' }}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-3" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setMobileMenuOpen(false)}
                  className="px-3 py-2.5 rounded-md text-sm font-medium"
                  style={{ color: isActivePath(link.path) ? '#f59e0b' : 'rgba(255,255,255,0.6)' }}>
                  {link.label}
                </Link>
              ))}
              <button onClick={handleLogout} className="mt-2 px-3 py-2.5 rounded-md text-sm font-medium text-left" style={{ color: '#ef4444' }}>
                {t('auth.logout')}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
